import { NgClass } from '@angular/common';
import { Component, effect, inject, signal, untracked } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonCard,
  IonContent,
  IonHeader,
  IonIcon,
  IonLabel,
  IonList,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { VxErrorComponent } from '@libs/core/lib/error-view/error-view.component';
import { VxLoadingComponent } from '@libs/core/lib/shared/loading-state';
import { VxDynamicImageComponent } from '@libs/core/lib/shared/mtjr-image.component';
import { AppAlerts } from '@libs/core/lib/utils/app-alerts';
import { withDeleteAddress } from '@libs/mtjr/shared/generated/entities/user/user-store-features';
import { Entity } from '@libs/mtjr/shared/generated/types';
import { UserStore } from '@libs/mtjr/shared/user/user.store';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { TranslatePipe } from '@ngx-translate/core';
import { ADDRESS_PROVIDER } from './address-provider.token';

export const AddressDeleteStore = signalStore(
  withDeleteAddress(),
  withState({ deletingAddress: '' }),
  withMethods((store) => ({
    async deleteAddressKeepingId(id: string) {
      patchState(store, { deletingAddress: id });
      return store.deleteAddress({ addressId: id });
    },
  })),
);

@Component({
  selector: 'mtjr-address',
  providers: [AddressDeleteStore],
  imports: [
    IonHeader,
    IonToolbar,
    IonTitle,
    IonText,
    IonContent,
    IonButton,
    VxLoadingComponent,
    VxErrorComponent,
    IonList,
    VxDynamicImageComponent,
    IonButton,
    IonIcon,
    IonLabel,
    IonButtons,
    IonBackButton,
    IonCard,
    IonSpinner,

    TranslatePipe,
    NgClass,
    RouterLink,
  ],
  template: `
    <ion-header [translucent]="true">
      <ion-toolbar>
        <ion-title>{{ (pickerMode() ? 'ADDRESS.SELECT_ADDRESS' : 'ADDRESS.MY_ADDRESSES') | translate }}</ion-title>
        <ion-buttons slot="start">
          <ion-back-button [defaultHref]="defaultBackHref" text="{{ 'COMMON.BACK' | translate }}" />
        </ion-buttons>
        <ion-buttons slot="end">
          <ion-button
            color="primary"
            [routerLink]="['create']"
            [relativeTo]="route"
            [queryParams]="route.snapshot.queryParams"
          >
            <ion-label class="capitalize">{{ 'COMMON.ADD' | translate }}</ion-label>
            <ion-icon slot="end" size="small" class="!me-2" name="location-add" />
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content>
      @switch (userDateStore.loggedInUserState().type) {
        @case ('loading') {
          <mtjr-loading />
        }
        @case ('error') {
          <mtjr-error [failure]="userDateStore.loggedInUserFailure()" />
        }
        @case ('success') {
          @if (userDateStore.addresses().length > 0) {
            <div class="flex w-full justify-center">
              <ion-list class="w-full max-w-[550px] !bg-transparent !pb-14">
                @for (address of userDateStore.addresses(); track $index) {
                  <div class="mx-4">
                    <ion-card
                      class="!mx-2 !my-2 cursor-pointer border-none"
                      [ngClass]="{
                        'hover:bg-primary/5 cursor-pointer': pickerMode(),
                      }"
                      (click)="pickerMode() ? onSelected(address) : undefined"
                    >
                      <div class="flex p-3">
                        <div class="flex flex-1 flex-col">
                          <div class="flex flex-col">
                            <div class="flex">
                              <ion-text class="flex-1 text-xl">{{ address.label }}</ion-text>
                              @if (address.id === selectedAddressId() && pickerMode()) {
                                <ion-icon name="checkmark" class="text-success m-2 text-xl"></ion-icon>
                              }
                            </div>
                            <ion-text color="medium" class="mt-1">
                              {{ address.street }}
                            </ion-text>
                            <ion-text color="medium" class="mb-2 mt-2">
                              {{ 'ADDRESS.PHONE' | translate }}: {{ address.phone }}
                            </ion-text>
                            <div class="mt-2 flex">
                              <ion-button
                                shape="round"
                                fill="outline"
                                class="!p-0 z-50"
                                color="primary"
                                [routerLink]="[address.id, 'edit']"
                                [relativeTo]="route"
                                [queryParams]="route.snapshot.queryParams"
                                (click)="$event.stopPropagation()"
                              >
                                <ion-label>{{ 'COMMON.EDIT' | translate }}</ion-label>
                              </ion-button>
                              <div class="ms-1"></div>
                              @if (!pickerMode()) {
                                <ion-button
                                  fill="outline"
                                  color="danger"
                                  shape="round"
                                  (click)="deleteAddress(address.id); $event.stopPropagation()"
                                  [disabled]="
                                    addressDeleteStore.deleteAddressState().type === 'loading' &&
                                    addressDeleteStore.deletingAddress() === address.id
                                  "
                                >
                                  @if (
                                    addressDeleteStore.deleteAddressState().type === 'loading' &&
                                    addressDeleteStore.deletingAddress() === address.id
                                  ) {
                                    <ion-spinner class="me-2 h-4 w-4" />
                                  }
                                  <ion-label>{{ 'COMMON.REMOVE' | translate }}</ion-label>
                                </ion-button>
                              }
                            </div>
                          </div>
                        </div>
                      </div>
                    </ion-card>
                  </div>
                }
              </ion-list>
            </div>
          } @else {
            <div class="flex h-full w-full flex-col items-center justify-center">
              <mtjr-dynamic-img
                src="/assets/no_orders.svg"
                darkSrc="/assets/no_orders_dark.svg"
                class="size-[220px] opacity-80 dark:opacity-70"
              />
              <p class="text-medium/70 mt-2 text-center leading-normal">
                {{ 'ADDRESS.NO_ADDRESS' | translate }}
                <br />
                {{ 'ADDRESS.CREATE_ADDRESS_PROMPT' | translate }}
              </p>

              <ion-button
                color="primary"
                class="mt-4 capitalize"
                data-cy="create-product-button"
                [routerLink]="['/account/addresses', 'create']"
                [queryParams]="route.snapshot.queryParams"
              >
                <ion-label class="">{{ 'ADDRESS.CREATE_NEW_ADDRESS' | translate }}</ion-label>
                <ion-icon slot="start" class="!me-2" name="location-add" />
              </ion-button>
            </div>
          }
        }
      }
    </ion-content>
  `,
})
export class AddressPage {
  userDateStore = inject(UserStore);
  addressDeleteStore = inject(AddressDeleteStore);
  route = inject(ActivatedRoute);
  addressProvider = inject(ADDRESS_PROVIDER, { optional: true });
  pickerMode = signal(this.route.snapshot.queryParams['pickerMode'] === 'true');
  selectedAddressId = signal(this.addressProvider?.address()?.id ?? '');
  router = inject(Router);
  appAlerts = inject(AppAlerts);
  defaultBackHref = this.route.snapshot.queryParams['redirect'] || '/';

  async deleteAddress(addressId: string) {
    const confirm = await this.appAlerts.yesNoAlertTR({
      message: 'ADDRESS.DELETE_CONFIRMATION',
      header: 'ADDRESS.DELETE_CONFIRMATION_HEADER',
      yesCaption: 'COMMON.DELETE',
      role: 'destructive',
    });
    if (confirm) {
      this.addressDeleteStore.deleteAddressKeepingId(addressId);
    }
  }

  constructor() {
    this.userDateStore.fetchUserIfNeeded();

    this.route.queryParams.subscribe((params) => {
      this.pickerMode.set(params['pickerMode'] === 'true');
    });

    effect(() => {
      const address = this.addressProvider?.address();
      if (address) {
        untracked(() => {
          this.selectedAddressId.set(address.id || '');
        });
      }
    });

    effect(() => {
      if (this.addressDeleteStore.deleteAddressState().success) {
        untracked(() => {
          this.userDateStore.refreshLoggedInUser();
        });
      }
    });
  }
  onSelected(address: Entity.UserAddress) {
    const redirect = this.route.snapshot.queryParams['redirect'];
    if (redirect) {
      this.router.navigate([redirect], { queryParams: { selectedAddrId: address.id } });
    } else {
      this.router.navigate(['/']);
    }
  }
}
